























































import Vue from 'vue';
import { ApptScheduleSteps, AppointmentMessages, Routes, NotificationModal } from '@/constants';
import { componentSelector, convertTeamMemberData, getAppointmentRequest, getTeamMembersOfDesignRoles, mapAppointmentData, scheduleSteps, setAssignedMembersOfStage } from './services/service';
import BreadCrumbs from '@/components/bread-crumbs/BreadCrumbs.vue';
import AppointmentDetails from './components/AppointmentDetails.vue';
import Banner from './components/Banner.vue';
import TeamMember from './components/TeamMember.vue';
import Calendar from './components/Calendar.vue';
import Timeslots from './components/Timeslots.vue';
import { getAppointmentDetails, getAppointmentTypeDetails, getTeamMembers, createAppointment, getTimeZones, modifyAppointment, getAllAppointments, getAppointmentLocations, getSupervisorData, getDivisionDreesMembers } from '@/api';
import { mapGetters } from 'vuex';
import { Appointment, ApptBuyer, DesignCenterLocationElement, Member, Role, ScheduleApptDetails, Slot, SupervisorData, Timezone, User } from '@/types';
import { addDays, calculateDateTimeDifference, hideLoader, showLoader } from '@/util';

export default Vue.extend({
  name: 'ScheduleAppointments',
  components: {
    BreadCrumbs,
    Banner,
    AppointmentDetails,
    TeamMember,
    Calendar,
    Timeslots,
    ContactDreesModal: () => import('@/components/modals/ConfirmationModal.vue'),
    InfoIcon: () => import('@/assets/icons/InfoIcon.vue'),
    WarningIcon: () => import('@/assets/icons/Warning.vue')
  },
  data () {
    return {
      stepNumber: 0,
      timeSlots: [] as string[],
      teamMembers: [] as Member[],
      timeZones: [] as Timezone[],
      appointment: {
        appointmentType: {}
      } as Appointment,
      appointmentDetails: {
        date: '',
        timeSlot: {} as Slot,
        timeZone: {} as Timezone,
        teamMember: [] as Member[],
        location: ''
      } as ScheduleApptDetails,
      appointmentLocations: {} as DesignCenterLocationElement,
      eligibleTeamMembers: [] as Member[],
      ApptScheduleSteps,
      AppointmentMessages,
      appointmentId: '',
      appointmentTypeId: '',
      appointmentExists: false,
      isVisibleConfirmationModal: false,
      modalData: NotificationModal().ContactDreesTeamModal,
      stageAppointments: [] as Appointment[],
      designMembers: [] as Member[],
      assignedTeamMemberId: '',
      Routes
    }
  },
  computed: {
    ...mapGetters(['user', 'selectedJob', 'selectedJobDetails', 'signedIn', 'selectedJobBuyers']),
    currentStep (): string {
      return scheduleSteps[this.stepNumber];
    },
    currentComponent (): any {
      return componentSelector(this.currentStep, TeamMember, Calendar, Timeslots);
    },
    teamMembersByRole (): Member[] {
      const roles = this.appointment.appointmentType?.roles?.map((role: Role) => role.fullName.toLowerCase());
      const filteredMembers = this.teamMembers?.filter((member: Member) => roles?.includes(member.title.toLowerCase()));
      return this.appointment.appointmentType.isAssignedMemberOfStage ? this.teamMembers : filteredMembers;
    },
    isValidAppointmentDateSelected (): boolean {
      const { daysLimit, isBeforeSelected, stageDatePropertyValue } = this.appointment.appointmentType;
      if (daysLimit && this.appointmentDetails.date && stageDatePropertyValue) {
        const appointmentDate = new Date(this.appointmentDetails.date);
        return isBeforeSelected ? appointmentDate <= calculateDateTimeDifference(stageDatePropertyValue, daysLimit, true) : appointmentDate >= addDays(stageDatePropertyValue, daysLimit);
      }
      return true;
    },
    dateUnavailableWarning (): boolean {
      return this.appointmentExists && !this.isValidAppointmentDateSelected && this.currentStep === ApptScheduleSteps.APPOINTMENT_SCHEDULED;
    },
    isModifyMode (): boolean {
      return this.$route.name === Routes.MODIFY_APPOINTMENT;
    },
    scheduleInitialAppointment (): boolean {
      return this.appointment.appointmentType?.isAssignedMemberOfStage && !this.stageAppointments.length && !this.designMembers.length;
    },
    assignedMemberInactive (): boolean {
      return this.appointment.appointmentType?.isAssignedMemberOfStage && !this.teamMembersByRole.length;
    },
    displayErrorMessages (): boolean {
      return this.scheduleInitialAppointment || this.assignedMemberInactive || !this.teamMembersByRole.length;
    },
    showGlobalCTAs (): boolean {
      return this.displayErrorMessages || this.currentStep === ApptScheduleSteps.APPOINTMENT_SCHEDULED;
    },
    allBuyers (): ApptBuyer[] {
      return this.selectedJobBuyers.map((buyer: any) => ({
        contactId: buyer.contactId,
        email: buyer.email,
        firstName: buyer.firstName,
        lastName: buyer.lastName,
        name: `${buyer.firstName} ${buyer.lastName}`,
        buyerType: buyer.buyerType
      }))
    }
  },
  async mounted () {
    this.appointmentId = this.$route.query.appointmentId as string || '';
    this.appointmentTypeId = this.$route.params.appointmentTypeId || '';
    showLoader();
    await Promise.all([this.fetchAppointmentDetails(), this.fetchTimeZones(), this.fetchLocation()]);
    if (this.appointment.appointmentType.isAssignedMemberOfStage) {
      await this.fetchStageAppointments();
    }
    this.appointmentExists = true;
    hideLoader();
  },
  methods: {
    previous () {
      if (!this.stepNumber) {
        const name = (this.$route.query as any).prev;
        this.$router.push({ name });
      } else {
        this.handleBackClick();
      }
    },
    handleBackClick () {
      if (this.dateUnavailableWarning) {
        this.appointmentDetails.date = '';
        this.appointmentDetails.timeSlot = {} as Slot;
        this.appointmentDetails.teamMember = this.eligibleTeamMembers;
        this.stepNumber = 1;
      } else {
        this.stepNumber -= 1;
        if (!this.isModifyMode) {
          this.clearCurrentStepData();
        } else {
          this.resetDataToDefault();
        }
      }
    },
    async navigate () {
      if (process.env.VUE_APP_SHOW_MESSAGE !== 'true') {
        return;
      }
      if (this.appointment.appointmentType.isAssignedMemberOfStage) {
        let supervisorId = '';
        if (this.assignedTeamMemberId) {
          showLoader();
          const response = await getSupervisorData(+this.assignedTeamMemberId);
          hideLoader();
          if (response) {
            const supervisorData = response as SupervisorData;
            supervisorId = supervisorData.memberId.toString();
          }
          this.$router.push({
            name: Routes.TEAM_MEMBER_MESSAGE,
            params: {
              teamMemberId: supervisorId
            }
          });
        } else {
          this.$router.push({ name: Routes.MESSAGES });
        }
      } else {
        this.isVisibleConfirmationModal = true
      }
    },
    next () {
      this.stepNumber += 1;
    },
    async fetchActiveTeamMembers () {
      let response = {} as any;
      if (!this.appointment.appointmentType.allowDivisionTeamMembers) {
        response = await getTeamMembers(this.selectedJob);
      } else {
        response = await getDivisionDreesMembers(this.selectedJobDetails.divisionId, {});
      }
      const data = response.members as Member[];
      const activeTeamMembers = data.filter((member) => member.status.toLowerCase() === 'active');
      this.teamMembers = activeTeamMembers;
    },
    async fetchTimeZones () {
      this.timeZones = (await getTimeZones()).timeZones;
    },
    getAssignedMembersOfStage () {
      const teamMembers = new Set(this.stageAppointments.map((appt) => appt.teamMember));
      if (teamMembers.size) {
        this.assignedTeamMemberId = Array.from(teamMembers)[0].teamMemberId;
      }
      this.designMembers = getTeamMembersOfDesignRoles(this.appointment.appointmentType.roles, this.teamMembers);
      const data = convertTeamMemberData(Array.from(teamMembers), this.teamMembers);
      if (data.length || this.designMembers.length) {
        this.teamMembers = setAssignedMembersOfStage(this.designMembers, data, this.appointment?.teamMember?.teamMemberId);
        const existingTeamMember = this.appointmentDetails.teamMember[0];
        if (existingTeamMember && this.teamMembers.findIndex((member) => member.userId === existingTeamMember.userId) === -1) {
          this.appointmentDetails.teamMember = [];
        }
      } else {
        this.appointmentDetails.teamMember = [];
        this.teamMembers = [];
      }
    },
    async fetchAppointmentDetails () {
      if (this.appointmentId) {
        this.appointment = (await getAppointmentDetails(this.appointmentId)).appointment;
        await this.fetchActiveTeamMembers();
        this.appointmentDetails = this.appointment.appointmentDate ? mapAppointmentData(this.appointment, this.teamMembers, this.timeZones) : this.appointmentDetails;
      } else if (this.appointmentTypeId) {
        this.appointment.appointmentType = (await getAppointmentTypeDetails(this.appointmentTypeId, {
          homeId: this.selectedJob
        })).appointmentType;
      }
      if (!this.appointment.appointmentType.buffer) {
        this.appointment.appointmentType.buffer = +process.env.VUE_APP_APPOINTMENT_BUFFER_CONFIG;
      }
    },
    async fetchStageAppointments () {
      const params = {
        pageNumber: 0,
        pageSize: 10000,
        filterBy: {
          stage: [this.appointment.appointmentType.stage]
        },
        buyer: {
          jobNumber: this.selectedJob
        },
        appointmentScope: 'SCHEDULED'
      };
      const data = await getAllAppointments(params);
      this.stageAppointments = data.appointments as Appointment[];
      this.getAssignedMembersOfStage();
    },
    async fetchLocation () {
      this.appointmentLocations = await getAppointmentLocations(this.selectedJobDetails.areaId);
    },
    setLocation (location: string) {
      this.appointmentDetails.location = location;
    },
    setTeamMember (data: any) {
      const { teamMember, location } = data;
      this.appointmentDetails.teamMember[0] = teamMember;
      this.eligibleTeamMembers[0] = teamMember;
      this.setLocation(location);
      this.next();
    },
    setDate (date: string) {
      this.appointmentDetails.date = date;
      this.next();
    },
    setTime (data: any) {
      this.appointmentDetails.timeSlot = data.timeSlot;
      this.appointmentDetails.timeZone = data.timeZone;
      this.appointmentDetails.teamMember = [];
      this.appointmentDetails.teamMember[0] = data.teamMember;
      this.next();
    },
    selectAny (data: any) {
      const { teamMember, location } = data;
      this.appointmentDetails.teamMember = teamMember;
      this.eligibleTeamMembers = teamMember;
      this.setLocation(location);
      this.next();
    },
    async scheduleAppointment () {
      showLoader();
      const buyers = this.appointment.buyers?.length ? this.appointment.buyers : this.allBuyers;
      const data = getAppointmentRequest(this.appointment, this.appointmentDetails, this.user, this.selectedJobDetails, buyers, this.isModifyMode);
      if (!this.isModifyMode && !this.appointment.appointmentId) {
        await createAppointment(data);
      } else {
        await modifyAppointment(data);
      }
      hideLoader();
      this.$router.push({ name: Routes.APPOINTMENTS });
    },
    clearCurrentStepData () {
      switch (this.currentStep) {
        case ApptScheduleSteps.SELECT_TIME:
          this.appointmentDetails.timeSlot = {} as Slot;
          this.appointmentDetails.teamMember = this.eligibleTeamMembers;
          break;

        case ApptScheduleSteps.SELECT_DATE:
          this.appointmentDetails.date = '';
          break;

        case ApptScheduleSteps.SELECT_TEAM_MEMBER:
          this.appointmentDetails.teamMember = [] as Member[];
          this.eligibleTeamMembers = [] as Member[];
          this.appointmentDetails.location = '';
          break;

        default:
          break;
      }
    },
    resetDataToDefault () {
      const data = mapAppointmentData(this.appointment, this.teamMembers, this.timeZones);
      switch (this.currentStep) {
        case ApptScheduleSteps.SELECT_TIME:
          this.appointmentDetails.timeSlot = data.timeSlot;
          this.appointmentDetails.teamMember = this.eligibleTeamMembers;
          break;

        case ApptScheduleSteps.SELECT_DATE:
          this.appointmentDetails.date = data.date;
          break;

        case ApptScheduleSteps.SELECT_TEAM_MEMBER:
          this.appointmentDetails.teamMember = data.teamMember;
          this.eligibleTeamMembers = [] as Member[];
          this.appointmentDetails.location = data.location;
          break;

        default:
          break;
      }
    }
  }
});
